/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React from 'react';
import { Box, IconButton, InputBase, InputBaseComponentProps, styled } from '@mui/material';
import { colors } from '@farmlands/mui-theme';
import { TestIds } from '..//testIds';
import MaterialSymbol from './MaterialSymbol';
import { ceilToTwoDecimals } from '../features/seed-orders/storage/utils';

const CounterContainer = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '6px',
  backgroundColor: theme.colors.primary100,
  borderRadius: '32px',
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  padding: '10px',
  '& span': {
    color: theme.colors.grey900,
    fontSize: '16px',
    fontWeight: 700,
  },
  '&:disabled': {
    opacity: 0.5,
  },
  '&:hover:not([disabled])': {
    backgroundColor: theme.colors.primary200,
  },
  '&:active:not([disabled])': {
    backgroundColor: theme.colors.primary300,
  },
}));

const StyledTextField = styled(InputBase)(({ theme }) => ({
  background: theme.colors.primary100,
  padding: '4px',
  '& input': {
    fontWeight: 700,
    '&:hover': {
      borderBottom: `1px solid ${theme.colors.grey900}`,
    },
  },
}));

interface Props {
  value: number;
  onChange: (value: number, blur?: boolean) => void;
  sx?: React.CSSProperties;
  inputProps?: InputBaseComponentProps;
  type?: 'number' | 'decimal';
  disabled?: boolean;
  hideDownCount?: boolean;
}

export const Counter = (props: Props) => {
  const handleCountDown = () => {
    if (props.disabled) return;
    if (props.hideDownCount) return;
    if (props.value <= 1) return false;
    props.onChange(ceilToTwoDecimals(props.value - 1));
  };

  const handleCountUp = () => {
    if (props.disabled) return;
    props.onChange(ceilToTwoDecimals(props.value + 1));
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    blur?: boolean,
  ) => {
    if (props.disabled) return;
    if (props.type === 'decimal') {
      if (event.target.value === '') return false;
      if (parseFloat(event.target.value) < 0) props.onChange(0);
      props.onChange(parseFloat(event.target.value), blur);
    } else {
      if (event.target.value === '') return false;
      if (parseInt(event.target.value) < 1) return false;
      props.onChange(parseInt(event.target.value), blur);
    }
  };

  const inputStyle: React.CSSProperties = {
    textAlign: 'center',
    color: `${colors.grey900}`,
    padding: '0px 4px',
    margin: '0px 2px',
    minWidth: '24px',
    width: `${props.value.toString().length + 1}ch`,
  };

  return (
    <CounterContainer sx={props.sx}>
      <StyledIconButton
        disabled={props.disabled || props.hideDownCount}
        onClick={handleCountDown}
        aria-label='reduce'
        disableTouchRipple
        data-testid={TestIds.components.counter.removeButton}
      >
        <MaterialSymbol
          sx={{
            visibility: props.hideDownCount ? 'hidden' : 'visible',
          }}
        >
          Remove
        </MaterialSymbol>
      </StyledIconButton>
      <StyledTextField
        value={props.value}
        onFocus={(event) => {
          event.target.select();
        }}
        onBlurCapture={() => {
          props.onChange(props.value, true);
        }}
        disabled={props.disabled}
        inputProps={{ style: inputStyle, ...props.inputProps }}
        type='number'
        onChange={handleChange}
        data-testid={TestIds.components.counter.amount}
      />
      <StyledIconButton
        disabled={props.disabled}
        onClick={handleCountUp}
        aria-label='add'
        disableTouchRipple
        data-testid={TestIds.components.counter.addButton}
      >
        <MaterialSymbol>Add</MaterialSymbol>
      </StyledIconButton>
    </CounterContainer>
  );
};

export default Counter;
